import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import { Alert, Button, Divider, Stack, TextField, Typography } from '@mui/material';

import { useExternalPromoUserImport } from '~/hooks/apis/promo/useExternalPromoUserImport';
import useCurrentPromoBrand from '~/hooks/useCurrentPromoBrand';

import { ExternalUserImportPayload, ExternalUserImportResponse } from '~/services/PromoService';

type Props = {
  codeName: string;
};

type ImportResult = ExternalUserImportResponse['result'];

const DEFAULT_LIMIT = 25;

function SmartRecruitersPromoUserImport({ codeName }: Props) {
  const { promoBrand: brand } = useCurrentPromoBrand();
  const { enqueueSnackbar } = useSnackbar();
  const importMutation = useExternalPromoUserImport();
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [updatedAfter, setUpdatedAfter] = useState('');
  const [previewCursor, setPreviewCursor] = useState<string | undefined>();
  const [importCursor, setImportCursor] = useState<string | undefined>();
  const [finished, setFinished] = useState(false);
  const [preview, setPreview] = useState<ImportResult | undefined>();
  const [isImportingAll, setIsImportingAll] = useState(false);
  const [completedPages, setCompletedPages] = useState(0);
  const [totals, setTotals] = useState({ created: 0, existing: 0, unmatched: 0, failed: 0 });

  const buildPayload = (dryRun: boolean, cursor?: string): ExternalUserImportPayload => ({
    importType: 'smartRecruiter',
    destination: 'promoUser',
    codeName,
    promoBrand: brand,
    candidateStatus: 'REJECTED',
    dryRun,
    limit,
    ...(updatedAfter ? { updatedAfter: new Date(updatedAfter).toISOString() } : {}),
    ...(cursor ? { pageId: cursor } : {}),
  });

  const requestPage = async (dryRun: boolean, cursor?: string) => {
    const response = await importMutation.mutateAsync(buildPayload(dryRun, cursor));
    if (dryRun) {
      setPreview(response.result);
      setPreviewCursor(cursor);
      setFinished(false);
    }
    return response.result;
  };

  const previewPage = async () => {
    try {
      await requestPage(true, importCursor);
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Unable to preview SmartRecruiters users', {
        variant: 'error',
      });
    }
  };

  const addTotals = (result: ImportResult) => {
    setTotals((current) => ({
      created: current.created + result.import.created,
      existing: current.existing + result.import.existing,
      unmatched: current.unmatched + result.import.unmatched,
      failed: current.failed + result.import.failed,
    }));
  };

  const importPage = async (cursor?: string) => {
    const imported = await requestPage(false, cursor);
    addTotals(imported);
    setCompletedPages((current) => current + 1);
    setImportCursor(imported.nextPageId);
    setFinished(!imported.nextPageId);
    return imported;
  };

  const importCurrentPage = async () => {
    if (!preview) return;
    try {
      const imported = await importPage(previewCursor);
      setPreview(undefined);
      setPreviewCursor(undefined);
      if (!imported.nextPageId) setFinished(true);
      enqueueSnackbar('SmartRecruiters page imported successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Unable to import SmartRecruiters page', {
        variant: 'error',
      });
    }
  };

  const importAll = async () => {
    if (!preview) return;
    setIsImportingAll(true);
    try {
      let result = await importPage(previewCursor);
      while (result.nextPageId) {
        result = await importPage(result.nextPageId);
      }
      setPreview(result);
      setPreviewCursor(undefined);
      enqueueSnackbar('All SmartRecruiters pages imported successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'SmartRecruiters import stopped', {
        variant: 'error',
      });
    } finally {
      setIsImportingAll(false);
    }
  };

  const isBusy = importMutation.isPending || isImportingAll;
  const canImportPreview = Boolean(preview && !finished && previewCursor === importCursor);

  const resetImport = () => {
    setPreview(undefined);
    setPreviewCursor(undefined);
    setImportCursor(undefined);
    setFinished(false);
    setCompletedPages(0);
    setTotals({ created: 0, existing: 0, unmatched: 0, failed: 0 });
  };

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1">SmartRecruiters import</Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField
          label="Page size"
          type="number"
          value={limit}
          onChange={(event) => setLimit(Math.min(100, Math.max(1, Number(event.target.value))))}
          inputProps={{ min: 1, max: 100 }}
          disabled={isBusy || Boolean(preview)}
        />
        <TextField
          label="Updated after"
          type="datetime-local"
          value={updatedAfter}
          onChange={(event) => setUpdatedAfter(event.target.value)}
          InputLabelProps={{ shrink: true }}
          disabled={isBusy || Boolean(preview)}
        />
        <Button variant="contained" onClick={previewPage} disabled={isBusy || !codeName || finished}>
          Preview page
        </Button>
        {finished && (
          <Button variant="text" onClick={resetImport} disabled={isBusy}>
            Start new import
          </Button>
        )}
      </Stack>
      {preview && (
        <>
          <Alert severity="info">
            Preview contains {preview.users.length} users. Created: {preview.import.created}; existing:{' '}
            {preview.import.existing}; unmatched: {preview.import.unmatched}; failed: {preview.import.failed}.
          </Alert>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={importCurrentPage} disabled={isBusy || !canImportPreview}>
              Import this page
            </Button>
            <Button variant="contained" onClick={importAll} disabled={isBusy || !canImportPreview}>
              Import all
            </Button>
          </Stack>
          {(completedPages > 0 || totals.created > 0) && (
            <Typography variant="body2" color="text.secondary">
              Imported pages: {completedPages}. Created: {totals.created}; existing: {totals.existing}; unmatched:{' '}
              {totals.unmatched}; failed: {totals.failed}.
            </Typography>
          )}
          {!preview.nextPageId && <Typography variant="body2">This is the last available page.</Typography>}
          <Divider />
        </>
      )}
      {isImportingAll && <Typography variant="body2">Importing pages sequentially…</Typography>}
    </Stack>
  );
}

export default SmartRecruitersPromoUserImport;
